import { Box, FormHelperText, Grid } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FixedSelection } from 'src/modules/groupMessageEmail/models/fixedSelection';
import { FormFieldset } from 'src/shared/components/form/formFieldset';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { InferType } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { createParticipantsAndSendInvite, getEmailTemplate } from 'src/shared/services/eventService';
import { toast } from 'react-toastify';
import { SelectionTypeEnum } from 'src/modules/groupMessageEmail/models/selectionType.enum';
import { Colors } from 'src/shared/components/styles/colors';
import { Selection } from 'src/modules/groupMessageEmail/models/selection';
import { SelectionForm } from 'src/modules/groupMessageEmail/components/selectionForm';
import { EventParticipantFieldArray } from './eventParticipantFieldArray';
import { EventSendInviteRequest } from '../models/eventSendInviteRequest';
import { useSchemaCreateSendInvite } from '../schemas/useSchemaCreateSendInvite';

type EventInviteParticipantsModalProps = {
  modalOpen: boolean;
  eventId: number;
  onSave: () => void;
  onClose: () => void;
  callback: () => void;
  createParticipants: boolean;
};

export const EventInviteParticipantsModal = (props: EventInviteParticipantsModalProps) => {
  const { modalOpen, onSave, onClose, eventId, createParticipants } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const modalTop = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  const [serverErrors] = useState<string[]>([]);
  const [, setSelection] = useState<Selection | null>(null);
  const [, setFixedSelection] = useState<FixedSelection | null>(null);

  const inviteRequestSchema = useSchemaCreateSendInvite();
  type SendInviteCreateFormInterface = InferType<typeof inviteRequestSchema>;

  const formMethods = useForm<SendInviteCreateFormInterface>({
    resolver: yupResolver(inviteRequestSchema),
    defaultValues: {
      subject: '',
      body: '',
      speedFormValues: '',
      speedFormText: '',
      selectionType: SelectionTypeEnum.Selection,
      selection_id: 0,
      externalParticipants: [
        {
          email: '',
          firstname: '',
          lastname: '',
        },
      ],
    },
    mode: 'all',
  });
  const {
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = formMethods;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getEmailTemplate(eventId);
        if (isMounted()) {
          setValue('body', res.data);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };
    fetchData();
  }, [eventId, isMounted, setValue]);

  const onSubmit = useCallback(
    async (data: SendInviteCreateFormInterface) => {
      // Rensa bort externalparticipants med tomma värden
      const externalParticipantsToPost =
        data.externalParticipants && data.externalParticipants.length > 0
          ? data.externalParticipants.filter((participant) => participant?.email.trim() !== '')
          : [];

      const excludeMemberIds = data.excludedMemberFromSelection?.map((member) => member.member_id);

      const dataToPost: EventSendInviteRequest = {
        subject: data.subject,
        body: data.body,
        receivers: externalParticipantsToPost,
        selectionId: data.selection_id,
        speedFormValues: data.speedFormValues,
        createParticipants,
        excludeMemberIDs: excludeMemberIds,
      };

      ConsoleHelper.log('dataToPost: ', dataToPost);

      try {
        await createParticipantsAndSendInvite(eventId, dataToPost);
        if (isMounted()) {
          toast.success(<>{t('event:inviteSend')}</>);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error', e);
      } finally {
        if (isMounted()) onSave();
      }
    },
    [createParticipants, eventId, isMounted, onSave, t],
  );

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const ifNoRecievers = !isValid && errors.atLeastOneRequired !== undefined;
  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={createParticipants ? t('event:inviteParticipants') : t('event:sendInvite')}
      onClose={onHandleClose}
      onSave={onHandleSave}
      saveLabel={t('common:button.send')}
      disableEnforceFocus
      dialogContent={
        <Box
          ref={modalTop}
          sx={{ position: 'relative', border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none') }}
        >
          <LoadingOverlay loading={loading} />
          <Box sx={{ p: 2 }}>
            <GridWrapper>
              <Grid item xs={12} mt={1.5}>
                <FormProvider {...formMethods}>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <GridWrapper>
                      <Grid item xs={12} mt={1.5}>
                        <FormFieldset legend={t('event:recievers')} hasError={ifNoRecievers}>
                          <Box p={2}>
                            <SelectionForm
                              selectionFromServer={null}
                              fixedSelectionFromServer={null}
                              onSelection={(v) => setSelection(v)}
                              onFixedSelection={(v) => setFixedSelection(v)}
                            />
                            <EventParticipantFieldArray />
                          </Box>
                        </FormFieldset>
                        {ifNoRecievers && (
                          <FormHelperText sx={{ color: Colors.ChipColors.Red, fontStyle: 'italic', ml: '14px' }}>
                            {errors.atLeastOneRequired?.message}
                          </FormHelperText>
                        )}
                      </Grid>
                      <Grid item xs={12} mt={1.5}>
                        <ControlledTextField name='subject' label={t('event:formFields.heading')} />
                      </Grid>
                      <Grid item xs={12} mt={1.5} mb={1.5}>
                        <ControlledTextEditor rows={60} name='body' noMargin hidedataPropFields />
                      </Grid>
                    </GridWrapper>
                  </form>
                </FormProvider>
              </Grid>
            </GridWrapper>
          </Box>
        </Box>
      }
    />
  );
};
